/**
 * ElementDetail component
 * Displays detailed information about a selected element in a modal
 */
import React from 'react';
import styled from 'styled-components';
import { ElementData } from './Element';

// Extended interface to handle optional properties
interface ExtendedElementData extends ElementData {
  image?: { main: string };
  summary?: string;
  source?: string;
}

interface ElementDetailProps {
  element: ElementData | null;
  onClose: () => void;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DetailContent = styled.div`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 10px;
  padding: 30px;
  max-width: 800px;
  width: 100%;
  color: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: 90vh;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  color: #e94560;
  font-size: 24px;
  cursor: pointer;
  transition: color 0.3s;
  
  &:hover {
    color: #ff6b8b;
  }
`;

const ElementName = styled.h2`
  font-size: 2.5rem;
  margin: 0;
  color: #e94560;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 15px;
`;

const Label = styled.span`
  font-weight: bold;
  color: #e94560;
  margin-right: 5px;
`;

const Value = styled.span`
  color: #ffffff;
`;

const Summary = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin: 15px 0;
`;

const SourceLink = styled.a`
  color: #e94560;
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: #ff6b8b;
    text-decoration: underline;
  }
`;

const ElementDetail: React.FC<ElementDetailProps> = ({ element, onClose }) => {
  if (!element) return null;
  
  const extendedElement = element as ExtendedElementData;
  
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Overlay onClick={onClose}>
      <DetailContent onClick={handleContentClick}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <ElementName>
          {element.name} ({element.symbol})
        </ElementName>
        
        <InfoGrid>
          <div>
            <Label>Atomic Number:</Label>
            <Value>{element.number}</Value>
          </div>
          <div>
            <Label>Atomic Mass:</Label>
            <Value>{element.atomic_mass}</Value>
          </div>
          <div>
            <Label>Category:</Label>
            <Value>{element.category}</Value>
          </div>
          <div>
            <Label>Phase:</Label>
            <Value>{element.phase}</Value>
          </div>
        </InfoGrid>
        
        {extendedElement.summary && (
          <Summary>{extendedElement.summary}</Summary>
        )}
        
        {extendedElement.image && extendedElement.image.main && (
          <img 
            src={extendedElement.image.main} 
            alt={element.name} 
            style={{ maxWidth: '100%', borderRadius: '5px', maxHeight: '300px', objectFit: 'contain' }} 
          />
        )}
        
        {extendedElement.source && (
          <div>
            <Label>Learn more:</Label>
            <SourceLink href={extendedElement.source} target="_blank" rel="noopener noreferrer">
              Wikipedia
            </SourceLink>
          </div>
        )}
      </DetailContent>
    </Overlay>
  );
};

export default ElementDetail; 